import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { RootState } from '../../store/store';
import { Drone, droneLabel } from '../../types/drone';
import { fleetRefreshRequested } from '../../store/slices/dronesSlice';
import { commandService, FlightCommand } from '../../services/commandService';
import { ApiError } from '../../types/api';

/**
 * Flight controls for one drone (#578).
 *
 * The UI could not send a command of any kind. Everything that has ever flown
 * on this cluster was commanded by `fleetctl` or raw HTTP, so an operator
 * recovering an aircraft needed a terminal — and during #556 needed `kubectl`.
 *
 * ## What is offered comes from the server
 *
 * `drone.availableCommands` is computed by the API from the same validation the
 * write path runs. Nothing here decides which command suits which state: that
 * table drifted once already, between the API and the aircraft, in both
 * directions at once (#556). A third copy in a browser would be worse than the
 * two, because it is the one furthest from the vehicle.
 *
 * So an unrecognised command is simply not rendered, and a command the server
 * stops offering disappears without this file changing.
 */

/** The commands this panel offers; a route is flown from the Planner (#632). */
type PanelCommand = Exclude<FlightCommand, 'fly_route'>;

/** How each command is labelled, and what it does, in a pilot's words. */
const COMMANDS: Record<
  PanelCommand,
  { label: string; confirm: string; danger?: boolean }
> = {
  takeoff: {
    label: 'Take off',
    confirm:
      'This arms the aircraft and flies it to the altitude below. Make sure the area above it is clear.',
  },
  land: {
    label: 'Land',
    confirm: 'The aircraft will descend and land where it currently is.',
  },
  return_to_base: {
    label: 'Return to base',
    confirm:
      'The aircraft flies back to where it took off for this flight and lands there. An autopilot set to stop above home (RTL_ALT_FINAL above 0) hovers there instead, until you land it.',
  },
  emergency_land: {
    label: 'Emergency land',
    confirm:
      'The aircraft comes down immediately, wherever it is. Use this when a normal landing is not safe to wait for.',
    danger: true,
  },
};

/** Order the buttons deliberately: recovery first, the committing one last. */
const ORDER: PanelCommand[] = [
  'land',
  'return_to_base',
  'takeoff',
  'emergency_land',
];

const DEFAULT_TAKEOFF_ALTITUDE_M = 5;

interface FlightControlsProps {
  drone: Drone;
}

export const FlightControls: React.FC<FlightControlsProps> = ({ drone }) => {
  const dispatch = useDispatch();
  // Visibility only. Every command is authorised server-side regardless of
  // what is rendered, so a stale role here changes what is shown and nothing
  // about what is permitted.
  const roles = useSelector((state: RootState) => state.user.roles);
  const mayCommand = roles.includes('operator') || roles.includes('admin');

  const [pending, setPending] = useState<PanelCommand | null>(null);
  const [sending, setSending] = useState(false);
  const [altitude, setAltitude] = useState(String(DEFAULT_TAKEOFF_ALTITUDE_M));
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  const offered = ORDER.filter((c) =>
    (drone.availableCommands ?? []).includes(c)
  );

  if (!mayCommand || offered.length === 0) {
    return null;
  }

  const confirmAndSend = async () => {
    if (pending === null) {
      return;
    }
    setSending(true);
    setError(null);
    try {
      const parameters =
        pending === 'takeoff' ? { altitude_m: Number(altitude) } : {};
      await commandService.send(drone.id, pending, parameters);
      // "commanded", never "landed" (#556). The API accepting a command means
      // it was queued for an aircraft that will accept it — not that the
      // aircraft has done it. The whole of #556 is the gap between those two.
      setSent(`${COMMANDS[pending].label} commanded. Watch the drone's state.`);
      setPending(null);
      // The state this panel reads decides which commands are offered next, so
      // it has to be refetched rather than assumed.
      dispatch(fleetRefreshRequested());
    } catch (e) {
      // The server's own refusal, which names the states that would have
      // worked. Far more use than "request failed".
      const api = e as ApiError;
      setError(api.serverMessage || api.message || 'The command could not be sent.');
    } finally {
      setSending(false);
    }
  };

  const altitudeInvalid =
    pending === 'takeoff' &&
    (!Number.isFinite(Number(altitude)) || Number(altitude) <= 0);

  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        Flight controls
      </Typography>

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mt: 1 }}>
        {offered.map((command) => (
          <Button
            key={command}
            size="small"
            variant={command === 'emergency_land' ? 'text' : 'outlined'}
            color={COMMANDS[command].danger ? 'error' : 'primary'}
            onClick={() => {
              setError(null);
              setSent(null);
              setPending(command);
            }}
          >
            {COMMANDS[command].label}
          </Button>
        ))}
      </Stack>

      {sent && (
        <Alert severity="success" sx={{ mt: 1 }} role="status">
          {sent}
        </Alert>
      )}
      {/* Confirmed before it is sent, always. This arms an aircraft, and an
          accidental click must not fly it. */}
      <Dialog open={pending !== null} onClose={() => !sending && setPending(null)}>
        <DialogTitle>
          {pending && COMMANDS[pending].label} — {droneLabel(drone)}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pending && COMMANDS[pending].confirm}
          </DialogContentText>
          {/* Inside the dialog, not behind it (#578). The dialog stays open on
              a refusal so the operator can adjust and retry — and a modal
              renders everything under it inert, so an error placed in the
              panel would be invisible at exactly the moment it matters. */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }} role="alert">
              {error}
            </Alert>
          )}
          {pending === 'takeoff' && (
            <TextField
              autoFocus
              margin="dense"
              size="small"
              type="number"
              label="Takeoff altitude (m)"
              value={altitude}
              onChange={(e) => setAltitude(e.target.value)}
              error={altitudeInvalid}
              helperText={
                altitudeInvalid
                  ? 'Enter an altitude above zero.'
                  : 'Metres above the launch point.'
              }
              inputProps={{ min: 1, step: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={confirmAndSend}
            disabled={sending || altitudeInvalid}
            color={pending && COMMANDS[pending].danger ? 'error' : 'primary'}
            variant="contained"
          >
            {sending ? <CircularProgress size={18} /> : 'Send command'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
